import * as React from 'react';
import { OTHER_LINE_ITEM_DESCRIPTION_LABEL_ID } from '../constants/app';
import { getFieldDefaultValue } from '../utils/formUtils';
import { ILabel } from '../constants/interfaces';

const SYNTHETIC_FORM_STATE_KEYS = new Set([
  String(OTHER_LINE_ITEM_DESCRIPTION_LABEL_ID),
]);

// Define types for form state
export interface FormFieldValue {
  children?: unknown[];
  value: string | number | boolean;
}

export type FormState = Record<string, FormFieldValue>;

/**
 * Utility function to clear all form state values
 * @param prev - Previous form state
 * @returns Cleared form state with empty values
 */
function getClearedFormState(prev: FormState): FormState {
  const cleared: FormState = {};
  Object.keys(prev).forEach((key) => {
    cleared[key] = { children: [], value: '' };
  });
  return cleared;
}

/**
 * Custom hook for managing form state and related operations
 *
 * @param initialFormState - Initial form state from useFormLabels
 * @param setFormState - Setter function for form state
 * @param visibleFields - Array of currently visible form fields
 * @returns Object containing form state operations
 */
export function useFormState(
  initialFormState: FormState,
  setFormState: (state: FormState | ((prev: FormState) => FormState)) => void,
  visibleFields: ILabel[]
) {
  const [resetTrigger, setResetTrigger] = React.useState(0);

  /**
   * Clears all form data and triggers a reset
   */
  const clearFormData = React.useCallback(() => {
    setFormState((prev) => getClearedFormState(prev));
    setResetTrigger((prev) => prev + 1);
  }, [setFormState]);

  /**
   * Updates form state when visible fields change
   * - Preserves existing values for visible fields
   * - Adds default values for newly visible fields
   */
  // Use a ref to store the previous visible field IDs to prevent unnecessary updates
  const prevVisibleIdsRef = React.useRef<string>('');

  React.useEffect(() => {
    const currentVisibleIds = visibleFields
      .map((f) => String(f.labelId))
      .sort()
      .join(',');

    // Only update if the visible fields have actually changed
    if (currentVisibleIds === prevVisibleIdsRef.current) {
      return;
    }

    prevVisibleIdsRef.current = currentVisibleIds;

    setFormState((prev) => {
      const visibleIds = new Set(visibleFields.map((f) => String(f.labelId)));
      const newState: FormState = {};

      // Preserve existing values for visible fields (and synthetic keys e.g. other-description bucket)
      for (const key of Object.keys(prev)) {
        if (visibleIds.has(key) || SYNTHETIC_FORM_STATE_KEYS.has(key)) {
          newState[key] = prev[key];
        }
      }

      // Add default values for newly visible fields that don't have values yet
      visibleFields.forEach((field) => {
        const fieldKey = String(field.labelId);
        if (!newState[fieldKey]) {
          const defaultValue = getFieldDefaultValue(field);
          newState[fieldKey] = { value: defaultValue };
        }
      });

      return newState;
    });
  }, [visibleFields, setFormState]);

  return {
    clearFormData,
    resetTrigger,
  };
}
